/**
 * IR 고도 추정 — 정사·IR 박스 스케일 순수 계산.
 * Spec: docs/superpowers/specs/2026-08-25-ir-altitude-estimate-from-ortho-boxes-design.md
 */

import { polygonRingFromImgPosCoords } from '@/services/defect/defectMapIrAlignment';
import { coordinateUtils } from '@/utils/polygonToImageCalculator';

export const MIN_L_MAP_M = 0.3;
export const MIN_N_PX = 8;

/**
 * @param {{ L_map_m: number, N_px: number }} input
 */
export function isValidSampleInput(input) {
  const L = Number(input?.L_map_m);
  const N = Number(input?.N_px);
  return Number.isFinite(L) && Number.isFinite(N) && L >= MIN_L_MAP_M && N >= MIN_N_PX;
}

/**
 * @param {object} params
 * @param {number} params.L_map_m
 * @param {number} params.N_px
 * @param {number} params.imgWidth
 * @param {number|null|undefined} params.footprintWidth_m
 * @param {number|null|undefined} params.h0_m
 * @returns {{ gsd_m: number, W_star_m: number, hStar_m: number|null, deltaH_m: number|null } | null}
 */
export function computeSampleAltitude({
  L_map_m,
  N_px,
  imgWidth,
  footprintWidth_m,
  h0_m
}) {
  if (!isValidSampleInput({ L_map_m, N_px })) {
    return null;
  }
  const width = Number(imgWidth);
  if (!Number.isFinite(width) || width <= 0) {
    return null;
  }

  const gsd_m = L_map_m / N_px;
  const W_star_m = gsd_m * width;
  const W0 =
    footprintWidth_m == null || footprintWidth_m === ''
      ? NaN
      : Number(footprintWidth_m);
  const h0 = h0_m == null || h0_m === '' ? NaN : Number(h0_m);

  if (Number.isFinite(W0) && W0 > 0 && Number.isFinite(h0)) {
    const hStar_m = h0 * (W_star_m / W0);
    return {
      gsd_m,
      W_star_m,
      hStar_m,
      deltaH_m: hStar_m - h0
    };
  }

  return {
    gsd_m,
    W_star_m,
    hStar_m: null,
    deltaH_m: null
  };
}

/**
 * @param {number[]} values
 * @returns {number|null}
 */
export function median(values) {
  const nums = (values || [])
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (nums.length === 0) {
    return null;
  }
  const mid = Math.floor(nums.length / 2);
  if (nums.length % 2 === 1) {
    return nums[mid];
  }
  return (nums[mid - 1] + nums[mid]) / 2;
}

function quartile(sorted, q) {
  if (!sorted.length) {
    return null;
  }
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] == null) {
    return sorted[base];
  }
  return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
}

/**
 * Spec: |Δh − median| > max(1.5×IQR, 2.0m)
 * @param {Array<{ id: string, deltaH_m: number|null }>} samples
 * @returns {string[]}
 */
export function flagOutlierIds(samples) {
  const withDelta = (samples || []).filter(
    s => s && s.id != null && Number.isFinite(Number(s.deltaH_m))
  );
  if (withDelta.length < 4) {
    return [];
  }
  const sorted = withDelta.map(s => Number(s.deltaH_m)).sort((a, b) => a - b);
  const med = median(sorted);
  const q1 = quartile(sorted, 0.25);
  const q3 = quartile(sorted, 0.75);
  if (med == null || q1 == null || q3 == null) {
    return [];
  }
  const iqr = q3 - q1;
  const threshold = Math.max(1.5 * iqr, 2.0);
  return withDelta
    .filter(s => Math.abs(Number(s.deltaH_m) - med) > threshold)
    .map(s => String(s.id));
}

/**
 * @param {Array<{ id?: string, deltaH_m: number|null, hStar_m?: number|null, included?: boolean }>} samples
 * @returns {{ method: string, sampleCountUsed: number, sampleCountTotal: number, medianDeltaH: number, medianHStar: number|null, altCorrectionValue: number } | null}
 */
export function aggregateDeltaH(samples) {
  const list = Array.isArray(samples) ? samples : [];
  const included = list.filter(s => s && s.included !== false);
  const withDelta = included.filter(s => Number.isFinite(Number(s.deltaH_m)));
  if (withDelta.length < 3) {
    return null;
  }
  const deltas = withDelta.map(s => Number(s.deltaH_m));
  const hStars = withDelta
    .map(s => Number(s.hStar_m))
    .filter(Number.isFinite);
  const medianDeltaH = median(deltas);
  if (medianDeltaH == null) {
    return null;
  }
  return {
    method: 'median_delta_h',
    sampleCountUsed: withDelta.length,
    sampleCountTotal: list.length,
    medianDeltaH,
    medianHStar: median(hStars),
    altCorrectionValue: medianDeltaH
  };
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * @param {{ reportId: number|string, samples: object[] }} params
 * @returns {object|null}
 */
export function buildAltitudeEstimateResult({ reportId, samples }) {
  const list = Array.isArray(samples) ? samples : [];
  const aggregate = aggregateDeltaH(list);
  if (!aggregate) {
    return null;
  }
  return {
    reportId,
    kind: 'IR',
    altCorrectionValue: round2(aggregate.altCorrectionValue),
    aggregate: {
      ...aggregate,
      medianDeltaH: round2(aggregate.medianDeltaH),
      medianHStar:
        aggregate.medianHStar == null ? null : round2(aggregate.medianHStar),
      altCorrectionValue: round2(aggregate.altCorrectionValue)
    },
    samples: list
  };
}

/**
 * imgPosCoords 링의 첫 변(사진 윗변 TL→TR) 길이(m).
 * @param {object|null|undefined} imgPosCoords
 * @returns {number|null}
 */
export function footprintWidthFromImgPosCoords(imgPosCoords) {
  const ring = polygonRingFromImgPosCoords(imgPosCoords);
  if (!ring || ring.length < 4) {
    return null;
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed =
    first &&
    last &&
    Number(first[0]) === Number(last[0]) &&
    Number(first[1]) === Number(last[1]);
  const open = closed ? ring.slice(0, -1) : ring.slice();
  if (open.length < 4) {
    return null;
  }
  const a = open[0];
  const b = open[1];
  if (
    !a ||
    !b ||
    !Number.isFinite(Number(a[0])) ||
    !Number.isFinite(Number(a[1])) ||
    !Number.isFinite(Number(b[0])) ||
    !Number.isFinite(Number(b[1]))
  ) {
    return null;
  }
  const meters = coordinateUtils.calculateDistance(a, b);
  return Number.isFinite(meters) && meters > 0 ? meters : null;
}
